import React, { useRef, useEffect } from "react";
import { Box, useTheme } from "@mui/material";

const themes = {
  dark: {
    vars: {
      "--output-bg": "#111417",
      "--output-text": "#00e676",
    },
  },
  github: {
    vars: {
      "--output-bg": "#ffffff",
      "--output-text": "#0969da",
    },
  },
  dracula: {
    vars: {
      "--output-bg": "#1e1f29",
      "--output-text": "#50fa7b",
    },
  },
  monokai: {
    vars: {
      "--output-bg": "#33352e",
      "--output-text": "#a6e22e",
    },
  },
};

export default function HtmlCodeEditorOutput({
  code,
  language = "html", // "html" or "css"
  themeName: externalTheme
}) {
  const iframeRef = useRef(null);
  const muiTheme = useTheme();
  const isDark = muiTheme.palette.mode === "dark";

  const themeName = externalTheme !== undefined ? externalTheme : (isDark ? "dark" : "github");

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
    if (!iframeDoc) return;

    const { vars } = themes[themeName] || (isDark ? themes.dark : themes.github);
    const outputBg = vars["--output-bg"];
    const outputText = vars["--output-text"];

    const fullCode = `
      <html>
        <head>
          <meta charset="UTF-8">
          <style>
            body {
              background: ${outputBg};
              color: ${outputText};
              font-family: 'Fira Code', monospace;
              margin: 0;
              padding: 10px;
              transition: background 0.3s ease, color 0.3s ease;
            }
          </style>
          ${language === "css" ? `<style>${code}</style>` : ""}
        </head>
        <body>
          ${language === "html" ? code : ""}
        </body>
      </html>
    `;

    iframeDoc.open();
    iframeDoc.write(fullCode);
    iframeDoc.close();
  }, [code, language, isDark, themeName]);

  const { vars } = themes[themeName] || (isDark ? themes.dark : themes.github);
  const currentOutputBg = vars["--output-bg"];

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        borderRadius: 2,
        background: currentOutputBg,
        overflow: "hidden",
        border: `1px solid ${
          isDark ? "rgba(255,255,255,0.1)" : muiTheme.palette.divider
        }`,
        transition: "background 0.4s ease, border-color 0.3s ease",
      }}
    >
      <iframe
        ref={iframeRef}
        title="HTML Output"
        sandbox="allow-scripts allow-same-origin"
        style={{
          width: "100%",
          height: "100%",
          border: "none",
          background: currentOutputBg,
          transition: "background 0.4s ease",
        }}
      />
    </Box>
  );
}
